import { ReactNode } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { SxProps, Theme } from "@mui/material/styles";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Logo from "../../shared/ui/brand/Logo";

export const FORM_STEPS = ["Início", "Condições", "Dados Pessoais", "Plantões", "Revisar", "Concluído"];
export const FORM_MOBILE_QUERY = "(max-width:767.95px)";
export const HOW_TO_URL = "https://rebrand.ly/sot8ogf";
// F2: um texto só para o rodapé (versão desktop), usado em todos os passos.
export const WHATSAPP_TEXT = "Em caso de dúvidas, fale com nossa equipe de atendimento via WhatsApp ou Telefone: 11 95213-3226";

export function FormText({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
  return <Typography sx={{ fontSize: 15, lineHeight: 1.65, color: "text.primary", ...(sx as object) }}>{children}</Typography>;
}

export function FormTitle({ children, mobile, sx }: { children: ReactNode; mobile?: boolean; sx?: SxProps<Theme> }) {
  return (
    <Typography component="h2" sx={{ fontSize: mobile ? 18 : 22, fontWeight: 700, color: "primary.main", lineHeight: 1.35, ...(sx as object) }}>
      {children}
    </Typography>
  );
}

export function SectionLabel({ children, sx }: { children: ReactNode; sx?: SxProps<Theme> }) {
  return (
    <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600, color: "primary.main", ...(sx as object) }}>
      {children}
    </Typography>
  );
}

export function WhatsLine({ center }: { center?: boolean }) {
  return (
    <Box sx={{ mt: 3, display: "flex", gap: 1, alignItems: "flex-start", justifyContent: center ? "center" : "flex-start" }}>
      <WhatsAppIcon sx={{ color: "#1e8e4e", mt: "1px" }} aria-hidden />
      <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: "primary.main", textAlign: center ? "center" : "left" }}>{WHATSAPP_TEXT}</Typography>
    </Box>
  );
}

type ActionsProps = { mobile: boolean; step: number; canNext: boolean; loading: boolean; onBack: () => void; onNext: () => void };

function FormActions({ mobile, step, canNext, loading, onBack, onNext }: ActionsProps) {
  const counter = (
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap" }}>{`${step + 1} / 6 · ${FORM_STEPS[step]}`}</Typography>
  );
  const next = (
    <Button
      size="large"
      variant="contained"
      disabled={!canNext || loading}
      onClick={onNext}
      fullWidth={mobile}
      endIcon={loading ? <CircularProgress size={18} color="inherit" /> : step === 4 ? <SendIcon /> : <ChevronRightIcon />}
      sx={{ textTransform: "none", fontWeight: 600 }}
    >
      {step === 4 ? "Revisar e enviar" : "Continuar"}
    </Button>
  );
  const back =
    step > 0 ? (
      <Button size="large" variant="outlined" onClick={onBack} startIcon={<ChevronLeftIcon />} fullWidth={mobile} sx={{ textTransform: "none", fontWeight: 600 }}>
        Voltar
      </Button>
    ) : null;
  if (mobile) {
    return (
      <Box sx={{ bgcolor: "#fff", borderTop: "1px solid #e5e7eb", px: 2, pt: 1.5, pb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>{counter}</Box>
        <Box sx={{ display: "grid", gridTemplateColumns: back ? "1fr 1.6fr" : "1fr", gap: 1 }}>
          {back}
          {next}
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, pt: 3, borderTop: "1px solid #e5e7eb" }}>
      {counter}
      <Box sx={{ flex: 1 }} />
      {back}
      {next}
    </Box>
  );
}

export type FormLayoutProps = ActionsProps & { children: ReactNode };

export default function FormLayout({ mobile, step, canNext, loading, onBack, onNext, children }: FormLayoutProps) {
  const done = step >= 5;
  return (
    <Box sx={{ bgcolor: "#eef1f5", minHeight: "100vh", width: "100%", display: "flex", flexDirection: "column" }}>
      <Box component="header" sx={{ bgcolor: "primary.main", color: "#fff" }}>
        <Box
          sx={{
            maxWidth: 816,
            mx: "auto",
            px: mobile ? 2 : 3,
            py: mobile ? 1.5 : 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Logo SVG novo (ruling: o mockup da aba 7 aprovado substitui o PNG antigo no formulário). */}
          {mobile ? <Logo variant="horizontal" tone="negative" height={28} /> : <Logo variant="stacked" tone="negative" height={56} />}
          {step === 0 && (
            <Button
              component="a"
              href={HOW_TO_URL}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              color="secondary"
              size={mobile ? "small" : "medium"}
              startIcon={<PlayArrowIcon />}
              sx={{
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: mobile ? "normal" : "nowrap",
                lineHeight: 1.25,
                textAlign: "left",
                maxWidth: mobile ? 190 : "none",
                color: "#fff",
              }}
            >
              Clique aqui e veja como é simples ANTECIPAR
            </Button>
          )}
        </Box>
        {!done && (
          <LinearProgress
            variant="determinate"
            value={((step + 1) / 6) * 100}
            color="secondary"
            aria-label={`Passo ${step + 1} de 6`}
            sx={{ height: 4, bgcolor: "rgba(255,255,255,.14)" }}
          />
        )}
      </Box>
      <Box
        component="main"
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: mobile ? "none" : 816,
          mx: "auto",
          px: mobile ? 0 : 3,
          py: mobile ? 0 : 5,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
        }}
      >
        <Paper
          elevation={0}
          sx={{ p: mobile ? 2 : 5, pt: mobile ? 3 : 5, borderRadius: mobile ? 0 : 3, border: mobile ? "none" : "1px solid #e2e6ec", flex: mobile ? 1 : "none" }}
        >
          {children}
          {!mobile && !done && <FormActions mobile={false} step={step} canNext={canNext} loading={loading} onBack={onBack} onNext={onNext} />}
        </Paper>
        {!mobile && (
          <Typography sx={{ textAlign: "center", fontSize: 12, color: "text.secondary", mt: 3 }}>ANTECIPE PAGAMENTOS E PARTICIPAÇÕES S/A</Typography>
        )}
      </Box>
      {mobile && !done && <FormActions mobile step={step} canNext={canNext} loading={loading} onBack={onBack} onNext={onNext} />}
    </Box>
  );
}
